import {Injectable, Type} from '@angular/core';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {CallbackType, Config, FRAuth, FRCallback, Step, StepOptions, StepType} from '@forgerock/javascript-sdk';
import {ConfigurationService, Locale, LocaleService} from '@ciam/library';
import {firstValueFrom, from, Observable, switchMap, take, throwError, timeout} from 'rxjs';
import {Output} from '../mfa-pin/APIResponse';
import {State} from "../../../core/states/state";
import {PIProtect} from '@forgerock/ping-protect';
import {ExitEditState} from "../../../core/states/exit/exit.state";
import {OtpState} from "../../../core/states/otp/otp.state";
import {DeviceState} from "../../../core/states/device/device.state";
import {Action, RequestObj} from "@forgerock/javascript-sdk/src/config/interfaces";
import {EditState} from "../../../core/states/edit/edit.state";
import {Environment} from "../../../util/environment";

/** Name of the NameCallback output that holds its prompt/label. */
const PROMPT_OUTPUT_KEY = 'prompt';

/** Prompt of the ChoiceCallback that carries the loginType in the journey indicating the edit of Login Info. */
const LOGIN_INFO_EDIT = 'loginType';

/** Prompt of the TextInputCallback that carries the First Name in the journey indicating the edit of Contact Info. */
const CONTACT_INFO_EDIT = 'given_name';

/** Prompt of the NameCallback that carries the one time passcode in the journey. */
const OTP_PROMPT = 'One Time Passcode';

/** Name of the Stage that conveys the OTP step in the journey. */
const TRIGGER_OTP = 'contact_info-confirm_otp';

/** Id of the HiddenValueCallback that carries the delivery-channel preferences (masked address). */
const DELIVERY_PREFERENCES_ID = 'deliveryChannelPreferences';

/**
 * Request parameter that tells the journey to skip the "email already verified" short-circuit and
 * route straight to the change-email + OTP path. Without it the journey completes immediately for a
 * user whose current email is already verified.
 */
const VERIFY_WITH_OTP_ONLY_QUERY: StepOptions = { query: { verifyWithOtpOnly: 'true' } };

/** FR error codes that represent an invalid/expired OTP (retryable) rather than a system failure. */
const OTP_INVALID_CODES = ['USER.PIN.INVALIDOREXPIRED', 'PIN.VELOCITY.FAILURE', 'OTP.LIMIT.REACHED'];

/** ConfirmationCallback index the journey expects for each user action on the OTP page. */
export enum OtpAction {
  SUBMIT = 0,
  RESEND = 1,
  CANCEL = 2
}

/** ConfirmationCallback index the journey expects on the verify-options page. */
export enum OptionsAction {
  CONTINUE = 0,
  CHANGE_EMAIL = 1
}

type CallbackValueResolver = string | ((values: Record<string, unknown>) => unknown);

let uniqueId: number = 0;

/**
 * Drives the ForgeRock `user_email_verification` journey used when a user changes their
 * email address in the profile. Mirrors the WLGN post-login email-verification service so
 * both applications share the same journey contract.
 */
@Injectable({
  providedIn: 'root'
})
export class EmailVerificationService {
  private state?: Type<State>
  private locale: Locale | undefined;
  private activeUpdateProfile?: string;
  callbacks: Partial<Record<CallbackType, FRCallback[]>> = {};
  currentStep: any;
  currentStage?: string;
  frSDKUrl = '';
  respStep: any;

  constructor(
    private configService: ConfigurationService,
    private localeService: LocaleService) {
    this.localeService.watch$().subscribe({
      next: (locale) => this.locale = locale
    });
  }

  initFRConfig(updateProfile: string): Observable<any | Type<State>> {
    this.activeUpdateProfile = updateProfile;
    return this.configService.all$('env', 'forgeRock').pipe(
      take(1),
      timeout({
        first: 3_000, with: () => Environment.isProduction()
          ? `${this.configService.get('env.frUrl')}`
          : throwError(() => 'no default provided except PROD')
      }),
      switchMap(config => {
        this.frSDKUrl = `${config[0].frUrl}/json/realms/root/realms/alpha/authenticate?authIndexType=service&authIndexValue=${config[0].frTree}`;
        const addHeadersMiddleware = (
          req: RequestObj,
          action: Action,
          next: () => RequestObj
        ): void => {
          switch (action.type) {
            case 'START_AUTHENTICATE':
            case 'AUTHENTICATE':
              const headers = req.init.headers as Headers;
              headers.append('x-locale', this.localeService.locale.posix);
              req.url.searchParams.set('updateProfile', updateProfile);
              req.url.searchParams.set('_si', `${uniqueId++}`);
              break;
          }
          next();
        };
        Config.set({
          clientId: config[0].frClientId ? config[0].frClientId : 'ClientSDKAppLP',
          middleware: [addHeadersMiddleware],
          scope: 'openid profile email address phone',
          serverConfig: {
            baseUrl: config[0].frUrl,
            timeout: 8000
          },
          realmPath: 'alpha',
          tree: 'user-profile-update',
        });
        return this.nextStep(null, `Init ${updateProfile}`);
      })
    )
  }

  async nextStep(step: any, action?: string): Promise<Step | Type<State>> {
    this.currentStep = await FRAuth.next(step)
      .catch((error) => {
        throw this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', action);
      });
    return this.handleStep(action);
  }

  /**
    * Map an FR journey step to a success response or a rejected error.
  * - LoginSuccess / intermediate Step: OTP accepted or code resent -> resolve without error.
  * - Step carrying payload.description: journey signaled an invalid/expired OTP -> reject 400.
  * - LoginFailure: OTP error codes -> reject 400, anything else -> reject 500 (system unavailable).
  *
  * @param action - label used for logging/error context
  */
  handleStep(action?: string): Promise<Step | Type<State>> {
    if (this.currentStep?.payload?.description) {
      return this.handleDescriptionErrors(action);
    }
    switch (this.currentStep?.type as StepType) {
      case StepType.Step:
        this.initCallbacks();
        return this.resolveState();
      case StepType.LoginSuccess:
        return Promise.resolve(ExitEditState);
      case StepType.LoginFailure:
        return this.handle401Errors(action);
      default:
        this.clearJourney();
        return Promise.reject(this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', action));
    }
  }

  initCallbacks(): void {
    this.callbacks = this.currentStep?.callbacks.reduce((callbackMap: CallbackType[], callback: FRCallback) => {
      const callbackType = callback.getType() as CallbackType;
      callbackMap[callbackType] = [...(callbackMap[callbackType] ?? []), callback];
      return callbackMap;
    }, {} as Partial<Record<CallbackType, FRCallback[]>>);
  }

  /**---
   * Resolves the states based on Callbacks and 'stage'
   * -*/
  resolveState(): Promise<Step | Type<State>> {
    let callback: FRCallback = this.currentStep?.callbacks[0];
    if(callback?.getType() === CallbackType.PingOneProtectInitializeCallback || callback?.getType() === CallbackType.PingOneProtectEvaluationCallback) return Promise.resolve(DeviceState);
    if( (callback?.getType() === CallbackType.ChoiceCallback && callback.getOutputValue(PROMPT_OUTPUT_KEY) === LOGIN_INFO_EDIT) ||
      (callback?.getType() === CallbackType.TextInputCallback && callback.getOutputValue(PROMPT_OUTPUT_KEY) === CONTACT_INFO_EDIT)) {
      return Promise.resolve(EditState);
    }
    if(this.currentStep?.payload?.stage === TRIGGER_OTP) return Promise.resolve(OtpState);

  }

  get responseStep(): any {
    return this.respStep;
  }

  set responseStep(resp) {
    this.respStep = resp;
  }

  /**---
   * Makes the Ping Protect calls - for both Initialize and Evaluate
   * -*/
  async collectDeviceData(): Promise<any | Type<State>> {
    switch (this.currentStep?.callbacks[0]?.getType() as CallbackType) {
      case CallbackType.PingOneProtectInitializeCallback: {
        const config = this.currentStep?.callbacks[0].getConfig();
        try {
          // Initialize with configuration from callback
          await PIProtect.start(config);
        } catch (err) {
          this.currentStep?.callbacks[0].setClientError(err.message );
        }
        return this.nextStep(this.currentStep, 'Ping Protect Initialize');
      }
      case CallbackType.PingOneProtectEvaluationCallback: {
        const deviceData = await PIProtect.getData();
        this.currentStep.setCallbackValue(CallbackType.PingOneProtectEvaluationCallback, deviceData);
        return this.nextStep(this.currentStep, 'Ping Protect Evaluation');
      }

    }
  }

  getCallbacks(callbackType: CallbackType): FRCallback[] {
    return this.callbacks[callbackType] ?? [];
  }

  submitProfileData(values: Record<string, unknown>,
                    callbackMap: Record<string, CallbackValueResolver> = {}): Observable<any> {
    if (!this.currentStep) {
      return from(Promise.reject(this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', 'ForgeRock submit')));
    }

    Object.values(this.currentStep?.callbacks).flat().forEach((callback: FRCallback) => {
      const prompt = callback.getOutputValue(PROMPT_OUTPUT_KEY) as string;
      const resolver = prompt ? callbackMap[prompt] ?? prompt : undefined;
      const callbackValue = typeof resolver === 'function'
        ? resolver(values)
        : resolver && Object.prototype.hasOwnProperty.call(values, resolver)
          ? values[resolver]
          : undefined;
      if (callbackValue !== undefined) {
        callback.setInputValue(callbackValue);
      }
    });
    this.responseStep = this.currentStep;

    return from(this.nextStep(this.currentStep, 'Submit Profile data').then((response) => {
      return response;
    }));
  }

  cancelUpdateProfileData(): Observable<any> {
    if (!this.currentStep) {
      return from(Promise.reject(this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', 'ForgeRock submit')));
    }
    this.currentStep?.callbacks.forEach((callback: FRCallback) => {
      if (callback.getType() === CallbackType.ConfirmationCallback && callback.getOutputValue(PROMPT_OUTPUT_KEY) === '') this.currentStep?.callbacks[2].setInputValue(2); // set input for Confirmation Callback as 2 for Cancel option.
    });
    return from(this.nextStep(this.currentStep, 'Cancel Update Profile data'));
  }

  private getCallbackPrompt(callback: FRCallback): string | undefined {
    try {
      const prompt = callback.getOutputValue(PROMPT_OUTPUT_KEY);
      return typeof prompt === 'string' && prompt ? prompt : undefined;
    } catch {
      return undefined;
    }
  }

  private clearJourney(): void {
    this.activeUpdateProfile = undefined;
    this.currentStep = undefined;
    this.callbacks = {};
  }

  private buildError(status: number, code: string, action?: string): HttpErrorResponse {
    return {
      error: { errors: [{ code, message: '' }] },
      status,
      url: this.frSDKUrl,
      message: `Http failure response on '${action}': '${code}'`
    } as unknown as HttpErrorResponse;
  }

  handleDescriptionErrors(action?: string): Promise<Step | Type<State>> {
    switch (this.currentStep?.payload?.description) {
      case 'OLD.PASSWORD.INVALID':
      case 'USER.PIN.INVALIDOREXPIRED':
      case 'USERNAME.INVALID':
      case 'USER.ALREADY.EXISTS':
      case 'EMAILADDRESS.BLOCKED':
      case 'EMAIL.USERID.ALREADY.EXIST':
      case 'GIVENNAME.INVALID':
      case 'SURNAME.INVALID':
        return Promise.reject(this.buildError(HttpStatusCode.BadRequest, this.currentStep?.payload?.description, action));
      case 'SYSTEM.UNAVAILABLE':
        return Promise.reject(this.buildError(HttpStatusCode.InternalServerError, this.currentStep?.payload?.description, action));
      default: {
        return Promise.reject(this.buildError(HttpStatusCode.BadRequest, this.currentStep?.payload?.description, action));
      }
    }
  }

  handle401Errors(action?: string): Promise<Step | Type<State>> {
    this.currentStep = this.responseStep;
    if (this.currentStep?.payload?.message === 'LOGIN.UNSUCCESSFUL' || this.currentStep?.payload?.message === 'Login failure' || this.currentStep?.payload?.message === 'Unauthorized' || this.currentStep?.payload?.message === 'Login failure'){
      return Promise.reject(this.buildError(HttpStatusCode.InternalServerError, this.currentStep?.payload?.description, action));
    } else {
      return Promise.reject(this.buildError(HttpStatusCode.InternalServerError, this.currentStep?.payload?.description || '', action));
    }
  }

  /**
   * Submit the entered OTP for the email-verification journey.
   *
   * @param pin - one time passcode entered by the user
   * @returns resolves with an APIResponse on success; errors with status 400 (invalid/expired) or 500 (system)
   */
  verifyOtp(pin: string): Observable<any | Type<State>> {
    this.setOtpAction(OtpAction.SUBMIT);
    this.setOtpValue(pin);
    return from(this.submitStep('OTP Submit'));
  }

  /**
   * Request a new OTP by choosing "Request New Code" on the journey's confirmation callback.
   *
   * @returns resolves when a fresh code has been sent; errors on system failure
   */
  resendOtp(): Observable<any | Type<State>> {
    this.setOtpAction(OtpAction.RESEND);
    return from(this.submitStep('OTP Resend'));
  }

  continueVerification(): Observable<Step | Type<State>> {
    this.setOtpAction(OptionsAction.CONTINUE);
    return from(this.submitStep('Options Continue'));
  }

  changeEmail(): Observable<Step | Type<State>> {
    this.setOtpAction(OptionsAction.CHANGE_EMAIL);
    return from(this.submitStep('Options Change Email'));
  }

  /**
   * Submit a new email address on the change-email step of the journey.
   *
   * @param email - the new email address entered by the user
   * @returns resolves with an APIResponse on success; errors with status 400 (validation) or 500 (system)
   */
  submitNewEmail(email: string): Observable<any | Type<State>> {
    this.setEmailValue(email);
    return from(this.submitStep('Change Email Submit'));
  }

  /**
   * Read the masked delivery address (e.g. "mf***@f***.com") from the current OTP step.
   *
   * @returns the masked address, or '' when it cannot be resolved
   */
  getVerificationAddress(): string {
    const hidden = this.currentStep?.callbacks?.find((callback) =>
      callback.getType() === CallbackType.HiddenValueCallback &&
      callback.getOutputValue('id') === DELIVERY_PREFERENCES_ID);
    if (!hidden) {
      return '';
    }
    try {
      const preferences: Output = JSON.parse(hidden.getOutputValue('value'));
      const channels = preferences.deliveryChannelPreferences;
      const channel = channels?.find((entry) => entry.deliveryMethod === preferences.preferredDeliveryMethod)
        ?? channels?.[0];
      const method = channel?.deliveryMethods?.find((entry) => entry.type === channel.preferredDeliveryType)
        ?? channel?.deliveryMethods?.[0];
      return method?.value ?? '';
    } catch {
      return '';
    }
  }

  private submitStep(action: string): Promise<Step | Type<State>> {
    if (!this.currentStep) {
      return Promise.reject(this.buildError(HttpStatusCode.InternalServerError, 'SYSTEM.UNAVAILABLE', action));
    }
    this.responseStep = this.currentStep;
    return this.nextStep(this.currentStep);
  }

  private getFailureStatus(step: any): number {
    return OTP_INVALID_CODES.includes(step?.payload?.message) ? HttpStatusCode.BadRequest : HttpStatusCode.InternalServerError;
  }

  private setOtpAction(value: OtpAction | OptionsAction): void {
    this.currentStep?.callbacks
      ?.filter((callback) => callback.getType() === CallbackType.ConfirmationCallback)
      .forEach((callback) => callback.setInputValue(value));
  }

  private setOtpValue(pin: string): void {
    this.currentStep?.callbacks
      ?.filter((callback) => callback.getType() === CallbackType.NameCallback && callback.getOutputValue(PROMPT_OUTPUT_KEY) === OTP_PROMPT)
      .forEach((callback) => callback.setInputValue(pin));
  }

  private setEmailValue(email: string): void {
    this.currentStep?.callbacks
      ?.find((callback) => callback.getType() === CallbackType.StringAttributeInputCallback)
      ?.setInputValue(email);
  }

}
